import { useState, useMemo } from 'react';
import { useChatPermissions } from '../../hooks/useChatPermissions';
import { useChat } from '../../context/ChatContext';
import ChatListItem from './ChatListItem';
import GroupInvitations from './GroupInvitations';

/**
 * ChatSidebar - Left panel with chat list navigation
 * Apple-inspired Messages design with light/dark mode support
 */

export default function ChatSidebar({
  chats,
  selectedChatId,
  onSelectChat,
  onNewChat,
  isMobileOpen = false,
  onClose,
  onHome,
  currentUserId
}) {
  const {
    canUseGroupChats,
    canUseOrgChannels,
    hasOrganization
  } = useChatPermissions();

  const {
    groupInvitations,
    refreshGroupInvitations,
    archivedChats,
    unarchiveChatConversation,
    notificationsEnabled,
    notificationsSupported,
    enableNotifications
  } = useChat();

  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Categorize chats
  const categorizedChats = useMemo(() => {
    const categories = {
      direct: [],
      meeting: [],
      group: [],
      organization: []
    };

    chats.forEach(chat => {
      // Apply search filter
      if (searchQuery) {
        const name = chat.name || '';
        const slug = chat.channelSlug || '';
        const searchLower = searchQuery.toLowerCase();
        if (!name.toLowerCase().includes(searchLower) &&
            !slug.toLowerCase().includes(searchLower)) {
          return;
        }
      }

      switch (chat.type) {
        case 'dm':
          categories.direct.push(chat);
          break;
        case 'meeting':
          categories.meeting.push(chat);
          break;
        case 'group':
          if (canUseGroupChats) {
            categories.group.push(chat);
          }
          break;
        case 'org_channel':
          if (canUseOrgChannels && hasOrganization) {
            categories.organization.push(chat);
          }
          break;
        default:
          break;
      }
    });

    return categories;
  }, [chats, searchQuery, canUseGroupChats, canUseOrgChannels, hasOrganization]);

  // Category header component
  const CategoryHeader = ({ title, count, showUpgrade = false, upgradeText = '' }) => (
    <div className="flex items-center justify-between px-3 py-2">
      <span className="text-[#86868b] text-xs font-semibold uppercase tracking-wider">
        {title}
      </span>
      {showUpgrade ? (
        <span className="text-[10px] text-purple-500 dark:text-purple-400 uppercase tracking-wide font-medium">
          {upgradeText}
        </span>
      ) : count > 0 && (
        <span className="text-[#86868b] text-xs">{count}</span>
      )}
    </div>
  );

  // Check if there are any chats at all
  const hasAnyChatType = categorizedChats.direct.length > 0 ||
                         categorizedChats.meeting.length > 0 ||
                         categorizedChats.group.length > 0 ||
                         categorizedChats.organization.length > 0;

  const sidebarContent = (
    <>
      {/* Header */}
      <div className="p-4 border-b border-[#d2d2d7] dark:border-white/10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Home button */}
            <button
              onClick={onHome}
              className="w-9 h-9 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
              title="Back to Dashboard"
            >
              <svg className="w-4 h-4 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
            <h2 className="text-[#1d1d1f] dark:text-white font-semibold text-lg">Messages</h2>
          </div>
          <div className="flex items-center gap-2">
            {/* Notification toggle */}
            {notificationsSupported && (
              <button
                onClick={enableNotifications}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95 ${
                  notificationsEnabled
                    ? 'bg-[#06c]/20 text-[#06c]'
                    : 'bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 text-[#86868b]'
                }`}
                title={notificationsEnabled ? 'Notifications enabled' : 'Enable notifications'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </button>
            )}
            {/* New chat button */}
            <button
              onClick={onNewChat}
              className="w-9 h-9 rounded-full bg-[#06c] hover:bg-[#0055d4] flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-sm"
              title="New message"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#f5f5f7] dark:bg-white/10 border-0 rounded-xl text-[#1d1d1f] dark:text-white text-sm placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#06c]/50 transition-all"
          />
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Group Invitations */}
        {groupInvitations.length > 0 && (
          <GroupInvitations
            invitations={groupInvitations}
            onUpdate={refreshGroupInvitations}
          />
        )}

        {/* Direct Messages */}
        <div>
          <CategoryHeader title="Direct" count={categorizedChats.direct.length} />
          {categorizedChats.direct.length > 0 ? (
            <div className="space-y-1">
              {categorizedChats.direct.map((chat) => (
                <ChatListItem
                  key={chat.id}
                  chat={chat}
                  isSelected={selectedChatId === chat.id}
                  onClick={onSelectChat}
                  currentUserId={currentUserId}
                />
              ))}
            </div>
          ) : (
            <p className="text-[#86868b] text-[13px] px-3 py-3">No conversations yet</p>
          )}
        </div>

        {/* Meeting Chats */}
        {categorizedChats.meeting.length > 0 && (
          <div>
            <CategoryHeader title="Meetings" count={categorizedChats.meeting.length} />
            <div className="space-y-1">
              {categorizedChats.meeting.map(chat => (
                <ChatListItem
                  key={chat.id}
                  chat={chat}
                  isSelected={selectedChatId === chat.id}
                  onClick={onSelectChat}
                  currentUserId={currentUserId}
                />
              ))}
            </div>
          </div>
        )}

        {/* Group Chats */}
        {canUseGroupChats ? (
          categorizedChats.group.length > 0 && (
            <div>
              <CategoryHeader title="Groups" count={categorizedChats.group.length} />
              <div className="space-y-1">
                {categorizedChats.group.map(chat => (
                  <ChatListItem
                    key={chat.id}
                    chat={chat}
                    isSelected={selectedChatId === chat.id}
                    onClick={onSelectChat}
                    currentUserId={currentUserId}
                  />
                ))}
              </div>
            </div>
          )
        ) : (
          <div>
            <CategoryHeader title="Groups" showUpgrade upgradeText="Pro" />
            <div className="px-3 py-3">
              <p className="text-[#86868b] text-[13px]">
                Upgrade to Pro to create group chats
              </p>
            </div>
          </div>
        )}

        {/* Organization Channels */}
        {canUseOrgChannels && hasOrganization ? (
          categorizedChats.organization.length > 0 && (
            <div>
              <CategoryHeader title="Organization" count={categorizedChats.organization.length} />
              <div className="space-y-1">
                {categorizedChats.organization.map(chat => (
                  <ChatListItem
                    key={chat.id}
                    chat={chat}
                    isSelected={selectedChatId === chat.id}
                    onClick={onSelectChat}
                    currentUserId={currentUserId}
                  />
                ))}
              </div>
            </div>
          )
        ) : !canUseOrgChannels && (
          <div>
            <CategoryHeader title="Organization" showUpgrade upgradeText="Team" />
            <div className="px-3 py-3">
              <p className="text-[#86868b] text-[13px]">
                Team plan includes organization channels
              </p>
            </div>
          </div>
        )}

        {/* Archived Chats */}
        {archivedChats.length > 0 && (
          <div>
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#f5f5f7] dark:hover:bg-white/5 rounded-lg transition-colors"
            >
              <span className="text-[#86868b] text-xs font-semibold uppercase tracking-wider">
                Archived
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[#86868b] text-xs">{archivedChats.length}</span>
                <svg
                  className={`w-4 h-4 text-[#86868b] transition-transform ${showArchived ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            {showArchived && (
              <div className="space-y-1 mt-1">
                {archivedChats.map(chat => (
                  <div key={chat.id} className="relative group">
                    <ChatListItem
                      chat={chat}
                      isSelected={selectedChatId === chat.id}
                      onClick={onSelectChat}
                      currentUserId={currentUserId}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        unarchiveChatConversation(chat.id);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 w-8 h-8 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 flex items-center justify-center transition-all"
                      title="Unarchive"
                    >
                      <svg className="w-4 h-4 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4l3-3m0 0l3 3m-3-3v6" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {!hasAnyChatType && !searchQuery && (
          <div className="flex items-center justify-center h-80">
            <div className="text-center max-w-xs">
              <div className="w-16 h-16 bg-[#f5f5f7] dark:bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-[#1d1d1f] dark:text-white text-sm font-medium mb-1">No messages yet</h3>
              <p className="text-[#86868b] text-xs mb-4">
                Start a conversation with your contacts
              </p>
              <button
                onClick={onNewChat}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#06c] hover:bg-[#0055d4] rounded-full text-white text-xs font-medium transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Message
              </button>
            </div>
          </div>
        )}

        {/* No search results */}
        {searchQuery && !hasAnyChatType && (
          <div className="flex items-center justify-center h-80">
            <div className="text-center max-w-xs">
              <svg className="w-12 h-12 text-[#86868b] mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-[#86868b] text-sm">No results found</p>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:relative inset-y-0 left-0 w-80 bg-white/95 dark:bg-[#1c1c1e]/95 backdrop-blur-xl border-r border-[#d2d2d7] dark:border-white/10 flex flex-col z-50
        transform transition-transform duration-300 ease-out
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 lg:hidden w-8 h-8 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 flex items-center justify-center transition-colors"
        >
          <svg className="w-5 h-5 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {sidebarContent}
      </div>
    </>
  );
}
